// src/AdminComponents/adminroles.jsx
import React, { useEffect, useMemo, useState } from"react";

/* ------------------------- tiny fetch helper ------------------------- */
const api = async (path, init = {}) => {
 const r = await fetch(`http://localhost:5000/api/bootcamp/roles${path}`, {
 method: init.method ||"GET",
 headers: {"Content-Type":"application/json" },
 body: init.body,
 });
 const txt = await r.text();
 try {
 const j = JSON.parse(txt);
 if (!r.ok || j.ok === false) throw new Error(j.error ||"Request failed");
 return j;
 } catch {
 throw new Error("Failed to fetch");
 }
};

const BTN = ({ children, onClick, tone ="blue", disabled, style }) => {
  const bg =
    tone ==="blue" ?"var(--primary-blue)" :
    tone ==="cyan" ?"var(--secondary-blue)" :
    tone ==="red" ?"#ef4444" :
    tone ==="muted"?"var(--bg-main)" :"var(--bg-soft)";
  
  const textColor = 
    tone ==="muted" ?"var(--text-muted)" :"#fff";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ?"var(--accent-gray)" : bg,
        color: disabled ?"var(--text-muted)" : textColor,
        border: tone ==="muted" ?"1px solid var(--accent-gray)" : 0,
        borderRadius: 12,
        padding:"10px 20px",
        fontWeight: 700,
        cursor: disabled ?"not-allowed" :"pointer",
        marginRight: 10,
        whiteSpace:"nowrap",
        transition:"all 0.2s ease",
        boxShadow: (tone ==="blue" || tone ==="cyan") && !disabled ?"0 4px 12px rgba(43, 176, 230, 0.2)" :"none",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.target.style.opacity ="0.9";
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.target.style.opacity ="1";
      }}
    >
      {children}
    </button>
  );
};

const Card = ({ title, right, children }) => (
  <div
    style={{
      background:"var(--bg-section)",
      border:"1px solid var(--accent-gray)",
      borderRadius: 20,
      padding: 24,
      marginTop: 20,
      boxShadow:"0 20px 50px rgba(31, 138, 203, 0.1)",
    }}
  >
    <div
      style={{
        display:"flex",
        gap: 12,
        justifyContent:"space-between",
        alignItems:"center",
        marginBottom: 20,
        flexWrap:"wrap",
      }}
    >
      <div style={{ color:"var(--primary-blue)", fontWeight: 800, fontSize: 20, textTransform:"uppercase", letterSpacing:"1px" }}>
        {title}
      </div>
      <div style={{ display:"flex", gap: 8, flexWrap:"wrap" }}>{right}</div>
    </div>
    {children}
  </div>
);

const Table = ({
  columns,
  rows,
  getKey,
  selectable,
  selected,
  onToggle,
  onToggleAll,
}) => (
  <div style={{ overflowX:"auto", borderRadius: 12, border:"1px solid var(--accent-gray)" }}>
    <table style={{ width:"100%", borderCollapse:"collapse", background:"#FFFFFF" }}>
      <thead>
        <tr style={{ background:"var(--bg-header-footer)" }}>
          {selectable ? (
            <th style={{ width: 40, padding: 12 }}>
              <input
                type="checkbox"
                onChange={(e) => onToggleAll?.(e.target.checked)}
                checked={
                  rows.length > 0 && selected && selected.size === rows.length
                }
              />
            </th>
          ) : null}
          {columns.map((c) => (
            <th
              key={c.key}
              style={{
                color:"var(--text-heading)",
                textAlign:"left",
                padding: 14,
                borderBottom:"1px solid var(--accent-gray)",
                whiteSpace:"nowrap",
                fontSize:"12px",
                fontWeight:"bold",
                textTransform:"uppercase",
                letterSpacing:"1px"
              }}
            >
              {c.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length + (selectable ? 1 : 0)}
              style={{ padding: 24, color:"var(--text-muted)", textAlign:"center" }}
            >
              No data found.
            </td>
          </tr>
        ) : (
          rows.map((r) => {
            const id = getKey(r);
            const checked = selected?.has(id);
            return (
              <tr
                key={id}
                style={{ 
                  borderBottom:"1px solid var(--bg-main)", 
                  cursor: selectable ?"pointer" :"default",
                  transition:"background 0.2s"
                }}
                onClick={() => selectable && onToggle?.(id, !checked)}
                onMouseEnter={(e) => e.currentTarget.style.background ="var(--bg-main)"}
                onMouseLeave={(e) => e.currentTarget.style.background ="transparent"}
              >
                {selectable ? (
                  <td style={{ padding: 12 }}>
                    <input
                      type="checkbox"
                      checked={!!checked}
                      onChange={(e) => onToggle?.(id, e.target.checked)}
                    />
                  </td>
                ) : null}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{ padding: 14, color:"var(--text-body)", minWidth: 120, fontSize:"14px", fontWeight: 500 }}
                  >
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </div>
);

const Select = ({ value, onChange, options, style }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{
      background:"var(--bg-main)",
      color:"var(--text-heading)",
      border:"1px solid var(--accent-gray)",
      borderRadius: 12,
      padding:"10px 16px",
      marginRight: 12,
      minWidth: 180,
      outline:"none",
      cursor:"pointer",
      fontWeight:"600",
      ...style,
    }}
  >
    {options.map((o) => (
      <option key={o.value ?? o.label} value={o.value} style={{ background:"#FFFFFF", color:"var(--text-heading)" }}>
        {o.label}
      </option>
    ))}
  </select>
);

export default function AdminRoles() {
 const [tab, setTab] = useState("individuals"); // individuals | teams | projects

 const [individuals, setIndividuals] = useState([]);
 const [teamCandidates, setTeamCandidates] = useState([]);
 const [teams, setTeams] = useState([]);
 const [projects, setProjects] = useState([]);

 const [selUsers, setSelUsers] = useState(new Set());
 const [selTeamUsers, setSelTeamUsers] = useState(new Set());
 const [selTeams, setSelTeams] = useState(new Set());
 const [selProjects, setSelProjects] = useState(new Set());

 const [err, setErr] = useState("");
 const [projForInd, setProjForInd] = useState("");
 const [projForTeam, setProjForTeam] = useState("");

 const [roleFilter, setRoleFilter] = useState("");
 const [expFilter, setExpFilter] = useState("");

 const [randTeamSize, setRandTeamSize] = useState(2);
 const [randTeamRole, setRandTeamRole] = useState("");
 const [randTeamExp, setRandTeamExp] = useState("");
 const [teamName, setTeamName] = useState("");

 // random assignment controls
 const [indRandCount, setIndRandCount] = useState(3);
 const [teamAssignCount, setTeamAssignCount] = useState(0); // 0 => assign all available
 const [teamAssignRole, setTeamAssignRole] = useState("");
 const [teamAssignExp, setTeamAssignExp] = useState("");

 // Projects (create)
 const [projTitle, setProjTitle] = useState("");
 const [projRole, setProjRole] = useState("");
 const [projExp, setProjExp] = useState("");
 const [projRandCount, setProjRandCount] = useState(5);
 const [projRandPrefix, setProjRandPrefix] = useState("");

 const roles = useMemo(() => {
 const src = [...individuals, ...teamCandidates];
 return Array.from(
 new Set(src.map((x) => String(x.role ||"").trim()).filter(Boolean))
 ).sort();
 }, [individuals, teamCandidates]);

 const exps = useMemo(() => {
 const src = [...individuals, ...teamCandidates];
 return Array.from(
 new Set(
 src.map((x) => String(x.experience_level ||"").trim()).filter(Boolean)
 )
 ).sort();
 }, [individuals, teamCandidates]);

 const loadAll = async () => {
 setErr("");
 try {
 const [ind, cand, tms, pro] = await Promise.all([
 api("/individuals"),
 api("/candidates/teams"),
 api("/teams"),
 api("/projects"),
 ]);
 setIndividuals(ind.data || []);
 setTeamCandidates(cand.data || []);
 setTeams(tms.data || []);
 setProjects(pro.data || []);
 } catch (e) {
 setErr(e.message ||"Failed to fetch");
 }
 };
 useEffect(() => {
 loadAll();
 }, []);

 const toggleUser = (id, on) =>
 setSelUsers((s) => {
 const n = new Set(s);
 on ? n.add(id) : n.delete(id);
 return n;
 });
 const toggleTeamUser = (id, on) =>
 setSelTeamUsers((s) => {
 const n = new Set(s);
 on ? n.add(id) : n.delete(id);
 return n;
 });
 const toggleTeam = (id, on) =>
 setSelTeams((s) => {
 const n = new Set(s);
 on ? n.add(id) : n.delete(id);
 return n;
 });
 const toggleProject = (id, on) =>
 setSelProjects((s) => {
 const n = new Set(s);
 on ? n.add(id) : n.delete(id);
 return n;
 });

 /* -------- Individuals actions -------- */
 const assignManualUsers = async () => {
 if (!projForInd || selUsers.size === 0) return;
 const r = await api("/assign/individuals/manual", {
 method:"POST",
 body: JSON.stringify({
 projectId: projForInd,
 userIds: Array.from(selUsers),
 }),
 });
 console.log("Repos created / invites:", r.repos);
 setSelUsers(new Set());
 loadAll();
 };

 const assignRandomUsers = async () => {
 if (!projForInd || !indRandCount) return;
 const r = await api("/assign/individuals/random", {
 method:"POST",
 body: JSON.stringify({
 projectId: projForInd,
 role: roleFilter || null,
 experience: expFilter || null,
 count: Number(indRandCount),
 }),
 });
 console.log("Repos created / invites:", r.repos);
 setSelUsers(new Set());
 loadAll();
 };

 const unassignUsers = async () => {
 if (selUsers.size === 0) return;
 await api("/unassign/individuals", {
 method:"POST",
 body: JSON.stringify({ userIds: Array.from(selUsers) }),
 });
 setSelUsers(new Set());
 loadAll();
 };

 const deleteUsers = async () => {
 if (selUsers.size === 0) return;
 if (!window.confirm("Delete selected users from roster?")) return;
 await api("/users", {
 method:"DELETE",
 body: JSON.stringify({ userIds: Array.from(selUsers) }),
 });
 setSelUsers(new Set());
 loadAll();
 };

 /* -------- Team actions -------- */
 const createRandomTeams = async () => {
 await api("/teams/random", {
 method:"POST",
 body: JSON.stringify({
 size: randTeamSize,
 role: randTeamRole || null,
 experience: randTeamExp || null,
 }),
 });
 loadAll();
 };

 const createManualTeam = async () => {
 if (!teamName || selTeamUsers.size === 0) return;
 await api("/teams/manual", {
 method:"POST",
 body: JSON.stringify({
 name: teamName,
 userIds: Array.from(selTeamUsers),
 }),
 });
 setTeamName("");
 setSelTeamUsers(new Set());
 loadAll();
 };

 const addUsersToTeam = async () => {
 if (selTeams.size !== 1 || selTeamUsers.size === 0) {
 alert("Select exactly one team and some users");
 return;
 }
 const [teamId] = Array.from(selTeams);
 await api("/teams/add-members", {
 method:"POST",
 body: JSON.stringify({ teamId, userIds: Array.from(selTeamUsers) }),
 });
 setSelTeamUsers(new Set());
 loadAll();
 };

 const removeUsersFromTeam = async () => {
 if (selTeams.size !== 1 || selTeamUsers.size === 0) {
 alert("Select exactly one team and some users");
 return;
 }
 const [teamId] = Array.from(selTeams);
 await api("/teams/remove-members", {
 method:"POST",
 body: JSON.stringify({ teamId, userIds: Array.from(selTeamUsers) }),
 });
 setSelTeamUsers(new Set());
 loadAll();
 };

 const assignProjectsToTeams = async () => {
 if (!projForTeam || selTeams.size === 0) return;
 const r = await api("/assign/teams/manual", {
 method:"POST",
 body: JSON.stringify({
 projectId: projForTeam,
 teamIds: Array.from(selTeams),
 }),
 });
 console.log("Repos created / invites:", r.repos);
 setSelTeams(new Set());
 loadAll();
 };

 const assignProjectsToTeamsRandom = async () => {
 if (!projForTeam) return;
 const r = await api("/assign/teams/random", {
 method:"POST",
 body: JSON.stringify({
 projectId: projForTeam,
 role: teamAssignRole || null,
 experience: teamAssignExp || null,
 count: Number(teamAssignCount) || 0, // 0 => all available
 }),
 });
 console.log("Repos created / invites:", r.repos);
 loadAll();
 };

 const unassignProjectsFromTeams = async () => {
 if (selTeams.size === 0) return;
 await api("/unassign/teams", {
 method:"POST",
 body: JSON.stringify({ teamIds: Array.from(selTeams) }),
 });
 setSelTeams(new Set());
 loadAll();
 };

 const deleteTeams = async () => {
 if (selTeams.size === 0) return;
 if (!window.confirm("Delete selected teams?")) return;
 await api("/teams", {
 method:"DELETE",
 body: JSON.stringify({ teamIds: Array.from(selTeams) }),
 });
 setSelTeams(new Set());
 loadAll();
 };

 /* -------- Projects actions -------- */
 const createProjectManual = async () => {
 if (!projTitle.trim()) return;
 await api("/projects/manual", {
 method:"POST",
 body: JSON.stringify({
 title: projTitle.trim(),
 role: projRole || null,
 experience_level: projExp || null,
 }),
 });
 setProjTitle("");
 setProjRole("");
 setProjExp("");
 loadAll();
 };

 const createProjectsRandom = async () => {
 await api("/projects/random", {
 method:"POST",
 body: JSON.stringify({
 count: Number(projRandCount) || 1,
 role: projRole || null,
 experience: projExp || null,
 prefix: projRandPrefix ||"",
 }),
 });
 loadAll();
 };

 const deleteProjects = async () => {
 if (selProjects.size === 0) return;
 if (!window.confirm("Delete selected projects (will remove assignments)?"))
 return;
 await api("/projects", {
 method:"DELETE",
 body: JSON.stringify({ projectIds: Array.from(selProjects) }),
 });
 setSelProjects(new Set());
 loadAll();
 };

  return (
    <div
      style={{ background:"var(--bg-main)", minHeight:"100vh", padding: 24, color:"var(--text-body)" }}
    >
      <div
        style={{
          background:"var(--primary-blue)",
          color:"#fff",
          padding: 20,
          borderRadius: 16,
          fontWeight: 900,
          fontSize: 24,
          textTransform:"uppercase",
          letterSpacing:"2px",
          boxShadow:"0 10px 30px rgba(43, 176, 230, 0.3)",
          textAlign:"center",
          marginBottom: 30
        }}
      >
        Bootcamp Admin — Roles
      </div>

      {err && (
        <div
          style={{
            background:"rgba(239, 68, 68, 0.1)",
            border:"1px solid rgba(239, 68, 68, 0.3)",
            color:"#ef4444",
            padding: 14,
            borderRadius: 12,
            marginTop: 16,
            fontWeight:"bold"
          }}
        >
          Error: {err}
        </div>
      )}

      <div style={{ marginTop: 20, display:"flex", gap: 12, flexWrap:"wrap", justifyContent:"center" }}>
        <BTN
          tone={tab ==="individuals" ?"blue" :"muted"}
          onClick={() => setTab("individuals")}
        >
          Individuals
        </BTN>
        <BTN
          tone={tab ==="teams" ?"blue" :"muted"}
          onClick={() => setTab("teams")}
        >
          Teams
        </BTN>
        <BTN
          tone={tab ==="projects" ?"blue" :"muted"}
          onClick={() => setTab("projects")}
        >
          Projects
        </BTN>
        <BTN tone="muted" onClick={loadAll} style={{ marginLeft:"auto" }}>
          Refresh Data
        </BTN>
      </div>

      {/* Individuals */}
      {tab ==="individuals" && (
        <Card
          title="Assign projects to Individuals"
          right={
            <>
              <Select
                value={projForInd}
                onChange={setProjForInd}
                options={[
                  { value:"", label:"Choose project..." },
                  ...projects.map((p) => ({
                    value: p.project_id,
                    label: p.title,
                  })),
                ]}
              />
              <Select
                value={roleFilter}
                onChange={setRoleFilter}
                options={[
                  { value:"", label:"Any role" },
                  ...roles.map((r) => ({ value: r, label: r })),
                ]}
              />
              <Select
                value={expFilter}
                onChange={setExpFilter}
                options={[
                  { value:"", label:"Any experience" },
                  ...exps.map((e) => ({ value: e, label: e })),
                ]}
              />
              <BTN onClick={assignManualUsers}>Assign Manually</BTN>
              <input
                type="number"
                min={1}
                value={indRandCount}
                onChange={(e) => setIndRandCount(Number(e.target.value))}
                style={{
                  width: 100,
                  background:"var(--bg-main)",
                  color:"var(--text-heading)",
                  border:"1px solid var(--accent-gray)",
                  borderRadius: 12,
                  padding:"10px 16px",
                  outline:"none",
                  fontWeight:"bold"
                }}
                placeholder="Count"
              />
              <BTN onClick={assignRandomUsers}>Assign Randomly</BTN>
              <BTN tone="muted" onClick={unassignUsers}>
                Unassign
              </BTN>
              <BTN tone="red" onClick={deleteUsers}>
                Delete Users
              </BTN>
            </>
          }
        >
 <Table
 columns={[
 { key:"user_id", title:"User ID" },
 { key:"user_name", title:"User" },
 { key:"role", title:"Role" },
 { key:"experience_level", title:"Experience" },
 {
 key:"project_title",
 title:"Project",
 render: (r) => r.project_title || r.project_id ||"—",
 },
 {
 key:"repo_html_url",
 title:"Repo",
 render: (r) =>
 r.repo_html_url ? (
 <a href={r.repo_html_url} target="_blank" rel="noreferrer" style={{ color:"#7fd1ff" }}>
 Open Repo
 </a>
 ) :"—",
 },
 ]}
 rows={individuals}
 getKey={(r) => r.user_id}
 selectable
 selected={selUsers}
 onToggle={toggleUser}
 onToggleAll={(checked) =>
 setSelUsers(
 checked
 ? new Set(individuals.map((x) => x.user_id))
 : new Set()
 )
 }
 />
 </Card>
 )}

 {/* Teams */}
 {tab ==="teams" && (
 <>
 <Card
 title="Create Teams — Randomly"
 right={<BTN onClick={createRandomTeams}>Create Random Teams</BTN>}
 >
 <div
 style={{
 display:"flex",
 alignItems:"center",
 gap: 12,
 flexWrap:"wrap",
 }}
 >
 <div style={{ color:"#bbb" }}>Size</div>
 <BTN
 tone="muted"
 onClick={() => setRandTeamSize(Math.max(2, randTeamSize - 1))}
 >
 –
 </BTN>
 <div style={{ width: 28, textAlign:"center" }}>
 {randTeamSize}
 </div>
 <BTN
 tone="muted"
 onClick={() => setRandTeamSize(randTeamSize + 1)}
 >
 +
 </BTN>
 <Select
 value={randTeamRole}
 onChange={setRandTeamRole}
 options={[
 { value:"", label:"Any role" },
 ...roles.map((r) => ({ value: r, label: r })),
 ]}
 />
 <Select
 value={randTeamExp}
 onChange={setRandTeamExp}
 options={[
 { value:"", label:"Any experience" },
 ...exps.map((e) => ({ value: e, label: e })),
 ]}
 />
 </div>
 </Card>

 <Card
 title="Create Team — Manually (uses selected Candidates)"
 right={
 <>
 <BTN onClick={createManualTeam}>Create Manual Team</BTN>
 <BTN onClick={addUsersToTeam}>Add Users</BTN>
 <BTN tone="muted" onClick={removeUsersFromTeam}>
 Remove Users
 </BTN>
 </>
 }
 >
 <div style={{ display:"flex", gap: 8, flexWrap:"wrap" }}>
        <input
          placeholder="Team name"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          style={{
            background:"var(--bg-main)",
            color:"var(--text-heading)",
            border:"1px solid var(--accent-gray)",
            borderRadius: 12,
            padding:"10px 16px",
            width: 300,
            outline:"none",
            fontWeight:"bold"
          }}
        />
 </div>
 <div style={{ color:"#64748B", marginTop: 12, fontSize:"13px", fontStyle:"italic" }}>
 Tip: select candidates below, then create/modify teams.
 </div>
 </Card>

 <Card title="Team Candidates">
 <Table
 columns={[
 { key:"user_id", title:"User ID" },
 { key:"user_name", title:"User" },
 { key:"role", title:"Role" },
 { key:"experience_level", title:"Experience" },
 ]}
 rows={teamCandidates}
 getKey={(r) => r.user_id}
 selectable
 selected={selTeamUsers}
 onToggle={toggleTeamUser}
 onToggleAll={(checked) =>
 setSelTeamUsers(
 checked
 ? new Set(teamCandidates.map((x) => x.user_id))
 : new Set()
 )
 }
 />
 </Card>

 <Card
 title="Teams"
 right={
 <>
 <Select
 value={projForTeam}
 onChange={setProjForTeam}
 options={[
 { value:"", label:"Choose project..." },
 ...projects.map((p) => ({
 value: p.project_id,
 label: p.title,
 })),
 ]}
 />
 <BTN onClick={assignProjectsToTeams}>
 Assign Projects Manually
 </BTN>
 <Select
 value={teamAssignRole}
 onChange={setTeamAssignRole}
 options={[
 { value:"", label:"Any role" },
 ...roles.map((r) => ({ value: r, label: r })),
 ]}
 />
 <Select
 value={teamAssignExp}
 onChange={setTeamAssignExp}
 options={[
 { value:"", label:"Any experience" },
 ...exps.map((e) => ({ value: e, label: e })),
 ]}
 />
        <input
          type="number"
          min={0}
          value={teamAssignCount}
          onChange={(e) => setTeamAssignCount(Number(e.target.value))}
          style={{
            width: 100,
            background:"var(--bg-main)",
            color:"var(--text-heading)",
            border:"1px solid var(--accent-gray)",
            borderRadius: 12,
            padding:"10px 16px",
            outline:"none",
            fontWeight:"bold"
          }}
          placeholder="0 = all"
        />
 <BTN onClick={assignProjectsToTeamsRandom}>
 Assign Projects Randomly
 </BTN>
 <BTN tone="muted" onClick={unassignProjectsFromTeams}>
 Unassign Projects
 </BTN>
 <BTN tone="red" onClick={deleteTeams}>
 Delete Teams
 </BTN>
 </>
 }
 >
 <Table
 columns={[
 { key:"name", title:"Team" },
 { key:"role", title:"Role" },
 { key:"experience_level", title:"Experience" },
 {
 key:"members",
 title:"Members",
 render: (r) =>
 (r.members || [])
 .map((m) => m.user_name)
 .join(",") ||"—",
 },
 {
 key:"project_title",
 title:"Project",
 render: (r) => r.project_title || r.project_id ||"—",
 },
 {
 key:"repo_html_url",
 title:"Repo",
 render: (r) =>
 r.repo_html_url ? (
 <a href={r.repo_html_url} target="_blank" rel="noreferrer" style={{ color:"#7fd1ff" }}>
 Open Repo
 </a>
 ) :"—",
 },
 ]}
 rows={teams}
 getKey={(r) => r.team_id}
 selectable
 selected={selTeams}
 onToggle={toggleTeam}
 onToggleAll={(checked) =>
 setSelTeams(
 checked ? new Set(teams.map((t) => t.team_id)) : new Set()
 )
 }
 />
 </Card>
 </>
 )}

 {/* Projects */}
 {tab ==="projects" && (
 <>
 <Card
 title="Create Project — Manual"
 right={<BTN onClick={createProjectManual}>Create Project</BTN>}
 >
 <div style={{ display:"flex", gap: 12, flexWrap:"wrap" }}>
        <input
          placeholder="Project title"
          value={projTitle}
          onChange={(e) => setProjTitle(e.target.value)}
          style={{
            background:"var(--bg-main)",
            color:"var(--text-heading)",
            border:"1px solid var(--accent-gray)",
            borderRadius: 12,
            padding:"10px 16px",
            width: 320,
            outline:"none",
            fontWeight:"bold"
          }}
        />
 <Select
 value={projRole}
 onChange={setProjRole}
 options={[
 { value:"", label:"Any role / None" },
 ...roles.map((r) => ({ value: r, label: r })),
 ]}
 />
 <Select
 value={projExp}
 onChange={setProjExp}
 options={[
 { value:"", label:"Any experience / None" },
 ...exps.map((e) => ({ value: e, label: e })),
 ]}
 />
 </div>
 </Card>

 <Card
 title="Create Projects — Random"
 right={<BTN onClick={createProjectsRandom}>Create Random Projects</BTN>}
 >
        <div style={{ display:"flex", gap: 12, flexWrap:"wrap" }}>
          <input
            type="number"
            min={1}
            value={projRandCount}
            onChange={(e) => setProjRandCount(Number(e.target.value))}
            style={{
              width: 120,
              background:"var(--bg-main)",
              color:"var(--text-heading)",
              border:"1px solid var(--accent-gray)",
              borderRadius: 12,
              padding:"10px 16px",
              outline:"none",
              fontWeight:"bold"
            }}
            placeholder="Count"
          />
 <Select
 value={projRole}
 onChange={setProjRole}
 options={[
 { value:"", label:"Any role / None" },
 ...roles.map((r) => ({ value: r, label: r })),
 ]}
 />
 <Select
 value={projExp}
 onChange={setProjExp}
 options={[
 { value:"", label:"Any experience / None" },
 ...exps.map((e) => ({ value: e, label: e })),
 ]}
 />
        <input
          placeholder="Title prefix (optional)"
          value={projRandPrefix}
          onChange={(e) => setProjRandPrefix(e.target.value)}
          style={{
            background:"var(--bg-main)",
            color:"var(--text-heading)",
            border:"1px solid var(--accent-gray)",
            borderRadius: 12,
            padding:"10px 16px",
            width: 260,
            outline:"none",
            fontWeight:"bold"
          }}
        />
 </div>
 </Card>

 <Card
 title="Projects"
 right={<BTN tone="red" onClick={deleteProjects}>Delete Projects</BTN>}
 >
 <Table
 columns={[
 { key:"title", title:"Title" },
 { key:"role", title:"Role" },
 { key:"experience_level", title:"Experience" },
 { key:"created_at", title:"Created At" },
 ]}
 rows={projects}
 getKey={(r) => r.project_id}
 selectable
 selected={selProjects}
 onToggle={toggleProject}
 onToggleAll={(checked) =>
 setSelProjects(
 checked
 ? new Set(projects.map((p) => p.project_id))
 : new Set()
 )
 }
 />
 </Card>
 </>
 )}
 </div>
 );
}



